// src/components/results/MobilitySummary.js
import React, { useMemo, useState } from "react";
import {
  normalizeMobilityRows,
  matchMobilityByAddress,
  aggregateMobilityRows,
  filterByCities,
} from "../../utils/mobility";

import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement
} from "chart.js";
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const pct = (n, d = 1) => (Number.isFinite(n) ? (n * 100).toFixed(d) + "%" : "-");
const fmt = (n) => (Number(n) || 0).toLocaleString("tr-TR");

function Truncated({ children, className = "" }) {
  const text = children == null ? "" : String(children);
  return (
    <span className={`inline-block max-w-full truncate ${className}`} title={text}>
      {text}
    </span>
  );
}

export default function MobilitySummary({ plan, datasets }) {
  // ✅ Tüm hook’lar koşulsuz çağrılır
  const mobRows = useMemo(() => normalizeMobilityRows(datasets || []), [datasets]);

  const sites = useMemo(() => {
    const list = Array.isArray(plan?.addresses) ? plan.addresses : [];
    return list.map((a, i) => ({
      id: a._id || a.id || i,
      name: a.name || a.address,
      address: a.address,
      _raw: a,
    }));
  }, [plan]);

  const planCities = useMemo(() => {
    const list = Array.isArray(plan?.addresses) ? plan.addresses : [];
    return list.map((r) => r.city).filter(Boolean);
  }, [plan]);

  const siteSections = useMemo(() => {
    if (!sites.length || !mobRows.length) return [];
    return sites.map((site) => {
      const matched = matchMobilityByAddress(site, mobRows);
      return { site, rows: matched, agg: matched.length ? aggregateMobilityRows(matched) : null };
    });
  }, [sites, mobRows]);

  const anySiteMatch = useMemo(() => siteSections.some((s) => s.rows.length > 0), [siteSections]);

  const cityRows = useMemo(
    () => (!anySiteMatch && mobRows.length ? filterByCities(mobRows, planCities) : []),
    [anySiteMatch, mobRows, planCities]
  );
  const cityAgg = useMemo(
    () => (cityRows.length ? aggregateMobilityRows(cityRows) : null),
    [cityRows]
  );

  const globalAgg = useMemo(
    () => (!anySiteMatch && !cityAgg && mobRows.length ? aggregateMobilityRows(mobRows) : null),
    [anySiteMatch, cityAgg, mobRows]
  );

  const totalAgg = useMemo(() => {
    const allRows = siteSections.flatMap((s) => s.rows);
    return allRows.length ? aggregateMobilityRows(allRows) : null;
  }, [siteSections]);

  // (opsiyonel) lightbox state – hook olduğu için yukarıda olmalı
  const [lightbox, setLightbox] = useState({ open: false, src: null, title: null });
  const openLightbox = (src, title) => setLightbox({ open: true, src, title });
  const closeLightbox = () => setLightbox({ open: false, src: null, title: null });

  const STATIC_PHOTOS = [
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSV2zMcCfCIJopYISupCFC0V1gtcCoUbJQNGg&s",
    "https://dyson-h.assetsadobe2.com/is/image/content/dam/dyson/global-newsroom/emea/uae/3d-dooh-launch/3D-DOOH-image2.jpg?$responsive$&cropPathE=desktop&fit=stretch,1&wid=1920",
    "https://mir-s3-cdn-cf.behance.net/project_modules/hd/c4345038736139.576d248e479a9.jpg",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR4O31PnmMJW_lzWcwTWaIPBRPZbWQj4krtwg&s",
  ];

  // ✅ Tüm hook’lar çağrıldı; şimdi guard
  if (!plan || !mobRows.length) return null;

  return (
    <div className="card p-4 space-y-4">
      {totalAgg && (
        <div className="border border-pink-200 rounded-xl overflow-hidden bg-pink-50 mt-6">
          <div className="px-3 py-2 text-sm font-bold text-pink-700">
            Total Sonuçlar (Eşleşen Adresler)
          </div>

          <SummaryBlocks agg={totalAgg} />

          <div className="flex flex-wrap justify-center gap-6 py-4">
            <PieGroup agg={totalAgg} prefix="Kullanılan Mobil Operatör Dağılımı" title="Mobil Operatör" />
            <PieGroup agg={totalAgg} prefix="Sahip Olunan Telefon Markası Dağılımı" title="Telefon Markası" />
            <PieGroup agg={totalAgg} prefix="Sosyo Ekonomik Segment Dağılımı" title="Sosyo Ekonomik Segment" />
          </div>

          {/* <div className="flex justify-center py-4">
            <div className="w-full max-w-xl">
              <div className="text-center text-xs font-semibold mb-2">POI Bazlı Dağılım</div>
              <Bar
                data={{
                  labels: ["İşyeri", "Banka", "Okul", "Otel", "Benzin İstasyonu"],
                  datasets: [
                    {
                      label: "Adet",
                      data: [101, 65, 54, 23, 82],
                      backgroundColor: ["#60a5fa", "#fbbf24", "#34d399", "#f472b6", "#a78bfa"],
                      borderRadius: 4, // köşeler yumuşak
                    },
                  ],
                }}
                options={{
                  plugins: {
                    legend: { display: false },
                  },
                  scales: {
                    x: { ticks: { color: "#374151" } },
                    y: { ticks: { color: "#374151" }, beginAtZero: true, grid: { color: "#e5e7eb" } },
                  },
                }}
              />

            </div>
          </div> */}
        </div>
      )}

      {/* {siteSections.length > 0 && (
        <div className="mt-4">
          <div className="font-semibold mb-2">Adres Ünite Fotoğrafları</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {siteSections.map(({ site }, idx) => {
              const first =
                site._raw?.photos?.[0] ||
                site._raw?.images?.[0] ||
                site._raw?.photoUrls?.[0];
              const src = first || STATIC_PHOTOS[idx % STATIC_PHOTOS.length];
              const key = site.id || site.name || site.address || idx;
              return (
                <div key={key} className="flex flex-col items-start">
                  <div className="text-sm font-medium truncate max-w-[220px]" title={site.name || site.address}>
                    {site.name || site.address}
                  </div>
                  <div className="w-full h-72 rounded-md overflow-hidden mt-2 border border-slate-200 bg-white">
                    <img src={src} alt={site.name || site.address} className="w-full h-full object-cover" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )} */}

      <div className="font-semibold">Mobility Özeti (Adres Bazlı)</div>
      {siteSections.map(({ site, rows, agg }) => {
        const key = site.id || site.name || site.address;
        return (
          <details key={key} className="border border-slate-200 rounded-lg overflow-hidden mb-3">
            <summary className="bg-slate-50/70 px-3 py-2 text-sm cursor-pointer flex items-center justify-between">
              <div className="min-w-0">
                <Truncated className="font-medium block">{site.name || site.address}</Truncated>
                <div className="text-xs text-slate-500">{rows.length} eşleşme</div>
              </div>
              <div className="ml-4 text-slate-500">
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </div>
            </summary>
            <div className="p-3">
              {rows.length === 0 ? (
                <div className="text-xs text-slate-500">
                  Eşleşen satır bulunamadı (adres normalizasyonu + fuzzy eşleşme denendi).
                </div>
              ) : (
                <>
                  <SummaryBlocks agg={agg} />
                  <TopPointsList agg={agg} />
                </>
              )}
            </div>
          </details>
        );
      })}

      {/* Fallback'leri istersen aç */}
      {/* {!anySiteMatch && cityAgg && (...) } */}
      {/* {!anySiteMatch && !cityAgg && globalAgg && (...) } */}
    </div>
  );
}

/* --- alt parçalar --- */
function PieGroup({ agg, prefix, title }) {
  const entries = Object.entries(agg.shares || {}).filter(([k]) => k.startsWith(prefix));
  if (!entries.length) return null;

  // renk paleti – açık temada güzel görünür
  const COLORS = [
    "#60a5fa", // mavi
    "#34d399", // yeşil
    "#fbbf24", // sarı
    "#f472b6", // pembe
    "#a78bfa", // mor
    "#f87171", // kırmızı
    "#facc15", // turuncu-sarı
    "#2dd4bf", // turkuaz
    "#818cf8", // indigo
  ];

  return (
    <div className="w-64">
      <div className="text-center text-xs font-semibold mb-2">{title}</div>
      <Pie
        data={{
          labels: entries.map(([k]) =>
            k.replace(`${prefix} % `, "").replace(`${prefix} `, "")
          ),
          datasets: [
            {
              data: entries.map(([, v]) => v),
              backgroundColor: COLORS.slice(0, entries.length), // ✅ renkler
              borderWidth: 1,
              borderColor: "#fff",
            },
          ],
        }}
        options={{
          plugins: {
            legend: { display: true, position: "bottom", labels: { color: "#374151" } },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const value = context.parsed;
                  return `${context.label}: ${(value * 100).toFixed(1)}%`;
                }
              }
            }
          },
        }}
      />
    </div>
  );
}


function SummaryBlocks({ agg }) {
  const HIDDEN_KEYS = ["Toplam Tekil Kullanıcı", "Face", "Ünite Sayısı"];

  const shareEntries = Object.entries(agg.shares || {}).filter(
    ([k]) => !HIDDEN_KEYS.includes(k)
  );

  const shareList = shareEntries.sort((a, b) =>
    a[0].localeCompare(b[0], "tr", { sensitivity: "base" })
  );

  return (
    <div className="p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">

        {/* Toplam Tekil Kullanıcı */}
        <div className="px-4 py-3 rounded-xl border border-blue-200 bg-blue-50 shadow">
          <div className="text-xs text-blue-700 font-semibold mb-1">
            Toplam Tekil Kullanıcı
          </div>
          <div className="text-lg font-bold text-blue-900">
            {fmt(agg.totalUsers)}
          </div>
        </div>

        {/* Dinamik Oran Kartları */}
        {shareList.map(([k, v]) => (
          <div
            key={k}
            className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 shadow"
          >
            <Truncated className="text-xs text-slate-600 block font-medium mb-1 max-w-[180px]">
              {k}
            </Truncated>
            <div className="text-lg font-bold text-slate-900">
              {pct(v)}
            </div>
          </div>
        ))}

      </div>
    </div>
  );
}


function TopPointsList({ agg }) {
  const rows = agg.topPoints || [];
  const max = Math.max(...rows.map((r) => r.users), 1);
  return <div className="p-3 space-y-2">{/* burada listeyi istersen açarız */}</div>;
}
